import {
  BulkMessageFilterInput,
  BulkMessageSendOptionInput,
  CreateBulkMessageMutationInput,
  UpdateBulkMessageMutationInput,
  MessagePartInput,
} from "@/__generated__/graphql";
import {
  FormValuesFilters,
  FormValues,
  MessagePartInputExtended,
} from "../types";
import { FunnelStateMaps } from "@/util/funnelGroups";

// See https://github.com/facebook/lexical/blob/main/packages/lexical-markdown/src/MarkdownExport.ts
// function exportTextFormat
const ESCAPED_CHARACTERS_REGEX = /\\([*_`~\\])/g;

export function formToCreateApi({
  formValues,
  orgId,
  userId,
  draft,
}: {
  formValues: FormValues;
  orgId: string;
  userId: string;
  draft: boolean;
}): CreateBulkMessageMutationInput {
  const { name, ...rest } = formToApi(formValues);
  return {
    name: name || "",
    createdById: userId,
    draft: draft,
    organizationId: orgId,
    ...rest,
  };
}

export function formToUpdateApi({
  formValues,
  bulkMessageId,
  draft,
}: {
  formValues: FormValues;
  bulkMessageId: string;
  draft: boolean;
}): UpdateBulkMessageMutationInput {
  const { name, ...rest } = formToApi(formValues);
  return {
    name: name || "",
    bulkMessageId,
    draft: draft,
    ...rest,
  };
}

function formToApi(formValues: FormValues) {
  const { name, filters, sendOption, messageParts, ...rest } = formValues;

  const {
    assignedCounselors,
    commitIndex,
    firstTimeMessageRecipients,
    interests,
    location,
    majors,
    startTerm,
    studentType,
    funnelStates,
    ...otherFilters
  } = filters;

  const queryFilters = {
    assignedCounselorIds: assignedCounselors,
    commitIndexes: commitIndex,
    firstTimeMessageRecipients: [firstTimeMessageRecipients],
    interestIds: interests,
    locations: location,
    majorIds: majors,
    startTerms: startTerm,
    studentTypes: studentType,
    funnelStates: funnelStates
      ?.map((state) => {
        return FunnelStateMaps["SIDEBAR"][state];
      })
      .flat(),
    ...otherFilters,
  } as BulkMessageFilterInput;

  return {
    name,
    filters: Object.fromEntries(
      Object.entries(queryFilters).filter(([, v]) => v?.length),
    ),
    sendOption: fixSendOption(sendOption),
    messageParts: cleanMessagePartsForApi(messageParts),
    ...rest,
  };
}

function cleanMessagePartsForApi(
  messageParts?: MessagePartInputExtended[],
): MessagePartInput[] | undefined {
  if (!messageParts) return messageParts;

  return filterEmptyMessageParts(messageParts)
    .map((part) => {
      if (part.create?.body?.text?.length) {
        const { text } = part.create.body;
        part.create.body.text = text.replace(ESCAPED_CHARACTERS_REGEX, "$1");
        return part;
      } else if (part.update?.body?.text?.length) {
        const { text } = part.update.body;
        part.update.body.text = text.replace(ESCAPED_CHARACTERS_REGEX, "$1");
        return part;
      } else {
        return part;
      }
    })
    .map((part) => {
      if (part.update?.body && "url" in part.update.body) {
        return {
          ...part,
          update: {
            messagePartId: part.update.messagePartId,
          },
        } as MessagePartInput;
      }
      return part as MessagePartInput;
    });
}

export function formToFilterApi(
  filters: FormValuesFilters,
): BulkMessageFilterInput {
  const {
    assignedCounselors,
    commitIndex,
    firstTimeMessageRecipients,
    interests,
    location,
    majors,
    startTerm,
    studentType,
    funnelStates,
    ...otherFilters
  } = filters;

  const queryFilters = {
    assignedCounselorIds: assignedCounselors,
    commitIndexes: commitIndex,
    firstTimeMessageRecipients: [firstTimeMessageRecipients],
    interestIds: interests,
    locations: location,
    majorIds: majors,
    startTerms: startTerm,
    studentTypes: studentType,
    funnelStates: funnelStates
      ?.map((state) => {
        return FunnelStateMaps["SIDEBAR"][state];
      })
      .flat(),
    ...otherFilters,
  } as BulkMessageFilterInput;

  return Object.fromEntries(
    Object.entries(queryFilters).filter(([, v]) => v?.length),
  );
}

function fixSendOption(
  sendOption?: BulkMessageSendOptionInput,
): BulkMessageSendOptionInput | undefined {
  if (sendOption?.sendLater) {
    const {
      sendLater: { startSendingAt },
    } = sendOption;
    if (startSendingAt) {
      return { sendLater: { startSendingAt: startSendingAt.getTime() / 1000 } };
    } else {
      return undefined;
    }
  } else if (sendOption?.startCampaign) {
    const {
      startCampaign: { startSendingAt, stopSendingAt },
    } = sendOption;
    if (startSendingAt && stopSendingAt) {
      return {
        startCampaign: {
          startSendingAt: startSendingAt.getTime() / 1000,
          stopSendingAt: stopSendingAt.getTime() / 1000,
        },
      };
    } else {
      return undefined;
    }
  } else {
    return sendOption;
  }
}

export function filterEmptyMessageParts(
  messageParts?: MessagePartInputExtended[],
): MessagePartInputExtended[] {
  if (!messageParts?.length) {
    return [];
  }

  return messageParts?.filter((part) => {
    if (part.create?.body && "text" in part.create.body) {
      return (
        !!part.create.body.text?.length &&
        part.create.body.text !== "__IMAGE_ELEMENT__"
      );
    } else if (part.update?.body && "text" in part.update.body) {
      return (
        !!part.update.body.text?.length &&
        part.update.body.text !== "__IMAGE_ELEMENT__"
      );
    } else {
      return true;
    }
  });
}
